/**
 * QA's `?as_of=` date simulation, kept in the address bar.
 *
 * The URL is the transport on purpose: it survives a reload, it is visible, and
 * a URL pasted into a bug report reproduces itself. The cost is that any page
 * rewriting its own query string will drop it — which is exactly what happened.
 * `LessonSequencingPage` rebuilt its params from `classroomId` and `dayOffset`,
 * and `StudentLessonScheduleView` replaced the whole query with `state`, so the
 * simulation silently expired seconds after load and every later request went
 * back to the real date.
 *
 * So preservation is a helper rather than a rule each writer has to remember.
 * `withAsOf` is what those writers call instead of building a bare object.
 */

const AS_OF = "as_of";

/** This page's simulated date, if any. Read from the live URL, not from state. */
export const currentAsOf = (): string | null =>
  new URLSearchParams(window.location.search).get(AS_OF);

/**
 * A page's own query params, plus `as_of` if the current URL carries one.
 *
 * Callers pass what they own; this adds only the thing they must not drop. It
 * never overwrites an explicit `as_of` a caller sets deliberately.
 */
export const withAsOf = (
  params: Record<string, string>,
): Record<string, string> => {
  if (AS_OF in params) return params;
  const asOf = currentAsOf();
  return asOf ? { ...params, [AS_OF]: asOf } : params;
};
